/**
 * Competition Detail Page
 *
 * Displays competition details with media sources and submission flow
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MediaCard from '../components/MediaCard';
import SubmissionEntryModal from '../components/SubmissionEntryModal';
import competitionService from '../services/competitionService';
import './CompetitionDetailPage.css';

const INITIAL_VISIBLE_SUBMISSIONS = 3;
const INITIAL_USER_SUBMISSIONS = 2;
const DESCRIPTION_PREVIEW_LENGTH = 180;

const DEFAULT_SECTION_STATE = {
  howTo: true,
  media: false,
  submissions: false,
  rules: false,
  scoring: false
};

const CompetitionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isAuthenticated } = useAuth();

  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [userEntries, setUserEntries] = useState([]);
  const [submissionEntries, setSubmissionEntries] = useState([]);
  const [shortlistedEntries, setShortlistedEntries] = useState([]);
  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [visibilityType, setVisibilityType] = useState('limited');
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState('');
  const [expandedSections, setExpandedSections] = useState(DEFAULT_SECTION_STATE);
  const [showAllSources, setShowAllSources] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllUserEntries, setShowAllUserEntries] = useState(false);
  const [showAllVisibleEntries, setShowAllVisibleEntries] = useState(false);
  const [showAllLeaderboardEntries, setShowAllLeaderboardEntries] = useState(false);
  const [showAllWinners, setShowAllWinners] = useState(false);

  const loadCompetition = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await competitionService.getById(id);
      const competitionData = data.competition || data;
      setCompetition(competitionData);

      setEntriesLoading(true);
      setEntriesError('');

      try {
        const visibleData = await competitionService.getVisibleSubmissions(id, { limit: 20, offset: 0 });

        const nextUserEntries = Array.isArray(visibleData.userSubmissions) ? visibleData.userSubmissions : [];
        const nextCuratedEntries = Array.isArray(visibleData.curatedSubmissions) ? visibleData.curatedSubmissions : [];
        const nextShortlisted = Array.isArray(visibleData.shortlistedSubmissions) ? visibleData.shortlistedSubmissions : [];
        const nextLeaderboard = Array.isArray(visibleData.leaderboard) ? visibleData.leaderboard : [];
        const nextWinners = Array.isArray(visibleData.winners) ? visibleData.winners : [];

        setUserEntries(sortEntriesByRecency(nextUserEntries));
        setSubmissionEntries(sortEntriesByRecency(nextCuratedEntries));
        setShortlistedEntries(nextShortlisted);
        setLeaderboardEntries(nextLeaderboard);
        setWinners(nextWinners);
        setVisibilityType(visibleData.visibilityType || competitionData.visibilityMode || 'limited');
      } catch (entriesLoadError) {
        setEntriesError('Unable to load entries right now.');
        setUserEntries([]);
        setSubmissionEntries([]);
        setShortlistedEntries([]);
        setLeaderboardEntries([]);
        setWinners([]);
      } finally {
        setEntriesLoading(false);
      }
    } catch (err) {
      console.error('Failed to load competition:', err);
      setError('Failed to load competition details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCompetition();
  }, [loadCompetition]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openSubmit') === '1') {
      if (isAuthenticated) {
        setShowSubmitModal(true);
      } else {
        navigate('/login', { replace: true, state: { from: { pathname: location.pathname } } });
      }
    }
  }, [isAuthenticated, location.pathname, location.search, navigate]);

  useEffect(() => {
    setExpandedSections(DEFAULT_SECTION_STATE);
    setShowAllSources(false);
    setShowFullDescription(false);
    setShowAllUserEntries(false);
    setShowAllVisibleEntries(false);
    setShowAllLeaderboardEntries(false);
    setShowAllWinners(false);
  }, [id]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      draft: 'status-draft',
      active: 'status-active',
      judging: 'status-judging',
      completed: 'status-completed',
      cancelled: 'status-cancelled'
    };
    return statusMap[status] || 'status-draft';
  };

  if (loading) {
    return (
      <div className="competition-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading competition...</p>
        </div>
      </div>
    );
  }

  if (error || !competition) {
    return (
      <div className="competition-detail-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Competition not found'}</p>
          <button onClick={() => navigate('/competitions')} className="back-button">
            Back to Competitions
          </button>
        </div>
      </div>
    );
  }

  const mediaSources = getMediaSources(competition);
  const primarySourceIndex = getPrimaryIndex(competition?.primarySourceIndex, mediaSources.length);
  const primarySource = mediaSources[primarySourceIndex] || null;
  const secondaryMediaSources = mediaSources.filter((_, index) => index !== primarySourceIndex);
  const hashtagLabel = formatHashtag(competition.requiredHashtag);
  const submissionPlatform = getSubmissionPlatform(primarySource, competition.platform);
  const availableSubmissionTypes = getAvailableSubmissionTypes(competition, mediaSources);
  const showMediaWarning = mediaSources.length === 0;
  const canEditCompetition = isEditableCompetitionStatus(competition.status) && canCurrentUserEditCompetition(competition, user, isAdmin);
  const totalSubmissions = Number(competition.totalSubmissions || 0);
  const participantCount = Number(competition.participantCount || 0);
  const submissionsLast24Hours = Number(competition.submissionsLast24Hours || 0);
  const validSubmissions = Number(competition.validSubmissions || 0);
  const submissionMode = getSubmissionMode(totalSubmissions);
  const phase = getCompetitionPhase(competition.status);
  const activeVisibleEntries = getActiveVisibleEntries(visibilityType, submissionEntries);
  const visibleEntries = phase === 'JUDGING'
    ? shortlistedEntries
    : phase === 'COMPLETED'
      ? submissionEntries
      : activeVisibleEntries;
  const validRate = getValidRate(validSubmissions, totalSubmissions);
  const urgencyLabel = getUrgencyLabel(competition.status, competition.endDate);
  const pulseLabel = getPulseLabel(phase, submissionMode, submissionsLast24Hours);
  const joinPrompt = getJoinPromptByMode(phase, submissionMode, visibilityType);
  const activitySignal = getActivitySignal(phase, submissionMode, visibilityType);
  const participantFillPercent = getParticipantFillPercent(participantCount, competition.maxParticipants);
  const shouldShowFullStats = phase === 'COMPLETED';
  const shouldShowApproxStats = phase === 'ACTIVE' && submissionMode === 'GROWING';
  const timeRemainingLabel = getTimeRemaining(competition.status, competition.endDate);
  const descriptionPreview = getDescriptionPreview(competition.description, DESCRIPTION_PREVIEW_LENGTH);
  const hasLongDescription = descriptionPreview !== competition.description;
  const descriptionText = showFullDescription ? competition.description : descriptionPreview;
  const benefitChips = getBenefitChips(competition.shortlistSize);
  const displayedUserEntries = getDisplayedItems(userEntries, showAllUserEntries, INITIAL_USER_SUBMISSIONS);
  const displayedVisibleEntries = getDisplayedItems(visibleEntries, showAllVisibleEntries, INITIAL_VISIBLE_SUBMISSIONS);
  const displayedLeaderboardEntries = getDisplayedItems(leaderboardEntries, showAllLeaderboardEntries, INITIAL_VISIBLE_SUBMISSIONS);
  const displayedWinners = getDisplayedItems(winners, showAllWinners, INITIAL_VISIBLE_SUBMISSIONS);
  const hasHiddenSubmissions = phase === 'ACTIVE' && visibilityType === 'hidden';

  const toggleSection = (sectionKey) => {
    setExpandedSections((currentState) => ({
      ...currentState,
      [sectionKey]: !currentState[sectionKey]
    }));
  };

  const handleCopyHashtag = () => {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(hashtagLabel);
    }
  };

  const handleOpenReels = () => {
    navigate(`/reels/${id}`);
  };

  const handleRequestSubmit = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: location.pathname } } });
      return;
    }

    setShowSubmitModal(true);
  };

  return (
    <div className="competition-detail-page">
      {/* Navigation */}
      <nav className="detail-nav">
        <button onClick={() => navigate('/competitions')} className="back-nav">
          ← Back to Competitions
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          {canEditCompetition && (
            <button onClick={() => navigate(`/competitions/${id}/edit`)} className="nav-link">
              Edit Competition
            </button>
          )}
          <button onClick={() => navigate(`/competitions/${id}/submissions`)} className="nav-link">
            View All Submissions
          </button>
          {isAuthenticated ? (
            <button onClick={() => navigate('/dashboard')} className="nav-link">
              Dashboard
            </button>
          ) : (
            <button onClick={() => navigate('/login')} className="nav-link">
              Login
            </button>
          )}
        </div>
      </nav>

      <div className="sticky-action-bar">
        <div className="sticky-action-meta">
          <span className="sticky-action-eyebrow">Competition</span>
          <strong className="sticky-action-title">{competition.name}</strong>
          <span className="sticky-action-time">{timeRemainingLabel}</span>
        </div>
        <div className="sticky-action-buttons">
          {competition.status === 'active' && (
            <button
              onClick={handleRequestSubmit}
              className="sticky-action-button"
            >
              Participate Now
            </button>
          )}
          <button
            type="button"
            onClick={handleOpenReels}
            className="sticky-action-button secondary"
          >
            View Media Sources
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-content">
        {/* Competition Header */}
        <div className="detail-header compact-hero">
          <div className="header-info">
            <h1>{competition.name}</h1>
            <span className={`status-badge ${getStatusBadgeClass(competition.status)}`}>
              {competition.status}
            </span>
          </div>

          <div className="header-meta">
            <div className="meta-item">
              <span className="icon">📅</span>
              <span>{formatDate(competition.startDate)} - {formatDate(competition.endDate)}</span>
            </div>
            <div className="meta-item">
              <span className="icon">📱</span>
              <span>{submissionPlatform}</span>
            </div>
          </div>

          <div className="benefit-chip-row">
            {benefitChips.map((chip) => (
              <span key={chip} className="benefit-chip">{chip}</span>
            ))}
          </div>

          {competition.description && (
            <div className="hero-description-card">
              <p>{descriptionText}</p>
              {hasLongDescription && (
                <button
                  type="button"
                  className="text-toggle-button"
                  onClick={() => setShowFullDescription((currentValue) => !currentValue)}
                >
                  {showFullDescription ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Two Column Layout */}
        <div className="detail-grid">
          {/* Left Column */}
          <div className="main-column">
            <AccordionSection
              title="How to Participate"
              isOpen={expandedSections.howTo}
              onToggle={() => toggleSection('howTo')}
            >
              <div className="instruction-card">
                {competition.requiredHashtag && (
                  <div className="compact-hashtag-row">
                    <span className="hashtag compact">{hashtagLabel}</span>
                    <button
                      onClick={handleCopyHashtag}
                      className="copy-button"
                      title="Copy hashtag"
                    >
                      Copy
                    </button>
                  </div>
                )}

                <ol className="creator-steps compact">
                  <li>Choose a media source below.</li>
                  <li>Create and publish your entry on {submissionPlatform}.</li>
                  <li>Include {hashtagLabel} in the caption or description.</li>
                  <li>Submit your video link here.</li>
                </ol>

                {showMediaWarning && (
                  <div className="permission-message compact-message">
                    <p>No media sources configured yet. You can still submit your entry.</p>
                  </div>
                )}

                {competition.status === 'active' ? (
                  <button
                    onClick={handleRequestSubmit}
                    className="submit-entry-button full-width-mobile"
                  >
                    Participate Now
                  </button>
                ) : competition.status === 'judging' ? (
                  <div className="permission-message compact-message">
                    <p>Submissions are closed. Results will be announced soon.</p>
                  </div>
                ) : (
                  <div className="permission-message compact-message">
                    <p>This competition is not currently accepting submissions.</p>
                  </div>
                )}
              </div>
            </AccordionSection>

            {/* Media Sources */}
            <AccordionSection
              title="Media Sources"
              isOpen={expandedSections.media}
              onToggle={() => toggleSection('media')}
              meta={mediaSources.length > 0 ? `${mediaSources.length} sources` : undefined}
            >
              <section className="media-sources-section compact-section-shell">
                {mediaSources.length === 0 ? (
                  <div className="empty-media-message">No media available for this competition</div>
                ) : (
                  <>
                    {primarySource && (
                      <div className="primary-media-wrapper">
                        <MediaCard source={primarySource} index={primarySourceIndex} isPrimary />
                      </div>
                    )}

                    {secondaryMediaSources.length > 0 && !showAllSources && (
                      <button
                        type="button"
                        className="secondary-toggle-button"
                        onClick={() => setShowAllSources(true)}
                      >
                        View All Sources
                      </button>
                    )}

                    {secondaryMediaSources.length > 0 && showAllSources && (
                      <>
                        <div className="media-grid-list">
                          {secondaryMediaSources.map((source, idx) => {
                            const sourceIndex = mediaSources.indexOf(source);
                            return (
                              <MediaCard
                                key={`${source.platform}-${source.url}-${idx}`}
                                source={source}
                                index={sourceIndex}
                                isPrimary={false}
                              />
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="secondary-toggle-button"
                          onClick={() => setShowAllSources(false)}
                        >
                          Show Less Sources
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      className="secondary-toggle-button"
                      onClick={handleOpenReels}
                    >
                      View Media Sources
                    </button>
                  </>
                )}
              </section>
            </AccordionSection>

            {/* Entry Momentum */}
            <AccordionSection
              title="Recent Submissions"
              isOpen={expandedSections.submissions}
              onToggle={() => toggleSection('submissions')}
              meta={totalSubmissions > 0 ? `${totalSubmissions} total` : undefined}
            >
            <section className="entries-section compact-section-shell">
              <h2>{getEntriesSectionTitle(phase)}</h2>

              {phase === 'ACTIVE' && submissionMode === 'EMPTY' && (
                <div className="empty-mode-box">
                  <p className="empty-mode-message">Be the first to participate and set the trend 🚀</p>
                  <button
                    onClick={handleRequestSubmit}
                    className="submit-entry-button"
                    disabled={competition.status !== 'active'}
                  >
                    Submit Entry
                  </button>
                </div>
              )}

              {phase === 'ACTIVE' && submissionMode === 'EARLY' && (
                <div className="signal-banner early">
                  <strong>🚀 Early Stage Advantage</strong>
                  <p>Less competition — higher chance to win</p>
                  <small>{activitySignal}</small>
                </div>
              )}

              {phase === 'ACTIVE' && submissionMode === 'GROWING' && (
                <div className="signal-banner growing">
                  <strong>Recent submissions</strong>
                  <p>{formatApproxSubmissionCount(totalSubmissions)} and climbing</p>
                  <small>{activitySignal}</small>
                </div>
              )}

              {phase === 'JUDGING' && (
                <div className="signal-banner mature">
                  <strong>Top entries under review</strong>
                  <p>Shortlisted submissions are currently being judged</p>
                </div>
              )}

              {phase === 'COMPLETED' && (
                <div className="signal-banner mature">
                  <strong>Top performers</strong>
                  <p>Winners and full leaderboard are now visible</p>
                </div>
              )}

              {entriesLoading && <p className="entries-state-text">Loading entries...</p>}
              {!entriesLoading && entriesError && <p className="entries-state-text error">{entriesError}</p>}

              {!entriesLoading && !entriesError && hasHiddenSubmissions && (
                <p className="entries-state-text">Submissions are hidden during active phase. Only your entries are visible.</p>
              )}

              {!entriesLoading && !entriesError && userEntries.length > 0 && (
                <div className="entries-subsection">
                  <h3>Your entries</h3>
                  <div className="entries-list">
                    {displayedUserEntries.map((entry, index) => (
                      <article key={entry.id || `${entry.videoUrl}-${index}`} className="entry-item">
                        <div className="entry-top-row">
                          <div>
                            <strong>{getEntryUserLabel(entry)}</strong>
                            <span className="entry-submeta">Submitted {formatDate(entry.submittedAt)}</span>
                          </div>
                          <span className="entry-score">{entry.status || 'pending'}</span>
                        </div>
                        <a href={entry.videoUrl} target="_blank" rel="noopener noreferrer" className="entry-link">
                          View your submission
                        </a>
                      </article>
                    ))}
                  </div>
                  {userEntries.length > INITIAL_USER_SUBMISSIONS && (
                    <button
                      type="button"
                      className="secondary-toggle-button"
                      onClick={() => setShowAllUserEntries((currentValue) => !currentValue)}
                    >
                      {showAllUserEntries ? 'Show Fewer Entries' : 'View More'}
                    </button>
                  )}
                </div>
              )}

              {!entriesLoading && !entriesError && visibleEntries.length > 0 && (
                <div className="entries-subsection">
                  {phase === 'ACTIVE' && <h3>Recent submissions</h3>}
                  {phase === 'JUDGING' && <h3>Top entries under review</h3>}
                  {phase === 'COMPLETED' && <h3>All submissions</h3>}
                  <div className="entries-list">
                  {displayedVisibleEntries.map((entry, index) => (
                    <article key={entry.id || entry.submissionId || `${entry.videoUrl}-${index}`} className="entry-item">
                      <div className="entry-top-row">
                        <div>
                          <strong>
                            {phase !== 'ACTIVE'
                              ? `#${entry.rank || index + 1} ${getEntryUserLabel(entry)}`
                              : getEntryUserLabel(entry)}
                          </strong>
                          <span className="entry-submeta">Submitted {formatDate(entry.submittedAt)}</span>
                        </div>
                        {phase !== 'ACTIVE' && (
                          <span className="entry-score">Score {formatEntryScore(entry.score)}</span>
                        )}
                      </div>
                      <a href={entry.videoUrl} target="_blank" rel="noopener noreferrer" className="entry-link">
                        View submission
                      </a>
                    </article>
                  ))}
                  </div>
                  {visibleEntries.length > INITIAL_VISIBLE_SUBMISSIONS && (
                    <button
                      type="button"
                      className="secondary-toggle-button"
                      onClick={() => setShowAllVisibleEntries((currentValue) => !currentValue)}
                    >
                      {showAllVisibleEntries ? 'Show Fewer Entries' : 'View More'}
                    </button>
                  )}
                </div>
              )}

              {!entriesLoading && !entriesError && leaderboardEntries.length > 0 && (
                <div className="entries-subsection">
                  <h3>{phase === 'JUDGING' ? 'Leaderboard (partial)' : 'Leaderboard'}</h3>
                  <div className="entries-list">
                    {displayedLeaderboardEntries.map((entry, index) => (
                      <article key={entry.submissionId || `${entry.userId}-${index}`} className="entry-item">
                        <div className="entry-top-row">
                          <strong>#{entry.rank || index + 1} {getEntryUserLabel(entry)}</strong>
                          <span className="entry-score">Score {formatEntryScore(entry.score)}</span>
                        </div>
                      </article>
                    ))}
                  </div>
                  {leaderboardEntries.length > INITIAL_VISIBLE_SUBMISSIONS && (
                    <button
                      type="button"
                      className="secondary-toggle-button"
                      onClick={() => setShowAllLeaderboardEntries((currentValue) => !currentValue)}
                    >
                      {showAllLeaderboardEntries ? 'Show Fewer Entries' : 'View More'}
                    </button>
                  )}
                </div>
              )}

              {!entriesLoading && !entriesError && winners.length > 0 && phase === 'COMPLETED' && (
                <div className="entries-subsection">
                  <h3>Winners</h3>
                  <div className="entries-list">
                    {displayedWinners.map((winner, index) => (
                      <article key={winner.submissionId || `${winner.userId}-${index}`} className="entry-item">
                        <div className="entry-top-row">
                          <strong>#{winner.rank || index + 1} {getEntryUserLabel(winner)}</strong>
                          <span className="entry-score">Score {formatEntryScore(winner.score)}</span>
                        </div>
                      </article>
                    ))}
                  </div>
                  {winners.length > INITIAL_VISIBLE_SUBMISSIONS && (
                    <button
                      type="button"
                      className="secondary-toggle-button"
                      onClick={() => setShowAllWinners((currentValue) => !currentValue)}
                    >
                      {showAllWinners ? 'Show Fewer Entries' : 'View More'}
                    </button>
                  )}
                </div>
              )}

              {!entriesLoading && !entriesError && submissionMode !== 'EMPTY' && visibleEntries.length === 0 && (
                <p className="entries-state-text">Entries will appear here as creators submit.</p>
              )}
            </section>
            </AccordionSection>

            <AccordionSection
              title="Rules & Details"
              isOpen={expandedSections.rules}
              onToggle={() => toggleSection('rules')}
            >
              <section className="description-section compact-section-shell">
                {competition.rules && competition.rules.length > 0 && (
                  <div className="details-block">
                    <h3 className="section-subtitle">Rules & Guidelines</h3>
                    <ul className="rules-list">
                      {competition.rules.map((rule, index) => (
                        <li key={index}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="details-grid">
                  <DetailItem label="Urgency" value={urgencyLabel} />
                  <DetailItem label="Platform" value={submissionPlatform} />
                  <DetailItem label="Shortlist Size" value={`${competition.shortlistSize || 20} videos`} />
                  <DetailItem label="Category" value={competition.category || 'General'} />
                  {competition.registrationDeadline && (
                    <DetailItem label="Registration Ends" value={formatDate(competition.registrationDeadline)} />
                  )}
                  {competition.maxParticipants && (
                    <DetailItem label="Max Participants" value={String(competition.maxParticipants)} />
                  )}
                  {Array.isArray(competition.allowedSubmissionTypes) && competition.allowedSubmissionTypes.length > 0 && (
                    <DetailItem label="Accepted Types" value={formatSubmissionTypes(competition.allowedSubmissionTypes)} />
                  )}
                  {competition.visibilityMode && competition.visibilityMode !== 'limited' && (
                    <DetailItem label="Submissions" value={competition.visibilityMode === 'public' ? 'All public during active phase' : 'Hidden until judging ends'} />
                  )}
                </div>

                {competition.prizes && competition.prizes.length > 0 && (
                  <div className="details-block">
                    <h3 className="section-subtitle">Prizes</h3>
                    <div className="prizes-list compact-prizes-list">
                      {competition.prizes.map((prize, index) => (
                        <div key={index} className="prize-item">
                          <span className="prize-position">#{index + 1}</span>
                          <span className="prize-value">{formatPrizeLabel(prize)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </AccordionSection>

            <AccordionSection
              title="Scoring & Fairness"
              isOpen={expandedSections.scoring}
              onToggle={() => toggleSection('scoring')}
            >
              <section className="description-section compact-section-shell">
                <div className="fairness-chip-row">
                  <span className="fairness-chip">Scores stay hidden until completion</span>
                  <span className="fairness-chip">Duplicate videos are blocked</span>
                  <span className="fairness-chip">Rate limits reduce spam</span>
                  <span className="fairness-chip">Best entries rise through ranking</span>
                </div>

                <div className="fairness-grid">
                  <div className="fairness-card">
                    <strong>Fair scoring</strong>
                    <p>Entries are ranked using the platform scoring flow so creators compete on a more level field.</p>
                  </div>
                  <div className="fairness-card">
                    <strong>Transparent phases</strong>
                    <p>Active competitions keep focus on participation first, then judging and final results open up progressively.</p>
                  </div>
                  <div className="fairness-card">
                    <strong>Quality checks</strong>
                    <p>Invalid or duplicate submissions can be flagged so ranking quality stays cleaner.</p>
                  </div>
                  <div className="fairness-card">
                    <strong>Current signal</strong>
                    <p>{shouldShowFullStats ? `${validRate}% of submissions are valid so far.` : activitySignal}</p>
                  </div>
                </div>
              </section>
            </AccordionSection>
          </div>

          {/* Right Column - Sidebar */}
          <div className="sidebar-column">
            {/* Competition Stats */}
            <div className="sidebar-card stats-card">
              <h3>Competition Pulse</h3>
              <div className="stats-hero">
                <span className="stats-hero-pill">{pulseLabel}</span>
                <p>{joinPrompt}</p>
                <small>{activitySignal}</small>
              </div>

              {shouldShowApproxStats && (
                <div className="stats-approx-chip">{formatApproxSubmissionCount(totalSubmissions)}</div>
              )}

              {shouldShowFullStats && (
                <div className="stats-highlight-grid">
                  <div className="stats-highlight-item">
                    <span className="stats-highlight-label">Total Submissions</span>
                    <strong className="stats-highlight-value">{totalSubmissions}</strong>
                  </div>
                  <div className="stats-highlight-item">
                    <span className="stats-highlight-label">Active Participants</span>
                    <strong className="stats-highlight-value">{participantCount}</strong>
                  </div>
                  <div className="stats-highlight-item">
                    <span className="stats-highlight-label">Valid Entries</span>
                    <strong className="stats-highlight-value">{validRate}%</strong>
                  </div>
                  <div className="stats-highlight-item">
                    <span className="stats-highlight-label">Submissions (24h)</span>
                    <strong className="stats-highlight-value">{submissionsLast24Hours}</strong>
                  </div>
                </div>
              )}

              <div className="stats-list">
                <div className="stat-item">
                  <span className="stat-label">Urgency:</span>
                  <span className="stat-value">{urgencyLabel}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Shortlist Size:</span>
                  <span className="stat-value">{competition.shortlistSize || 20} videos</span>
                </div>
                {competition.category && (
                  <div className="stat-item">
                    <span className="stat-label">Category:</span>
                    <span className="stat-value">{competition.category}</span>
                  </div>
                )}
                {competition.maxParticipants && (
                  <div className="stat-item">
                    <span className="stat-label">Max Participants:</span>
                    <span className="stat-value">{competition.maxParticipants}</span>
                    <div className="capacity-track" role="presentation">
                      <span className="capacity-fill" style={{ width: `${participantFillPercent}%` }}></span>
                    </div>
                  </div>
                )}
                {competition.registrationDeadline && (
                  <div className="stat-item">
                    <span className="stat-label">Registration Ends:</span>
                    <span className="stat-value">{formatDate(competition.registrationDeadline)}</span>
                  </div>
                )}
                <div className="stat-item">
                  <span className="stat-label">Platform:</span>
                  <span className="stat-value">{submissionPlatform}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Time Remaining:</span>
                  <span className="stat-value">{getTimeRemaining(competition.status, competition.endDate)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Required Hashtag:</span>
                  <span className="stat-value">{hashtagLabel}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="sidebar-card actions-card">
              <h3>Quick Actions</h3>
              {competition.status === 'active' && (
                <button 
                  onClick={handleRequestSubmit} 
                  className="action-button primary"
                >
                  📹 Submit Entry
                </button>
              )}
              
              <button 
                onClick={() => navigate('/competitions')} 
                className="action-button"
              >
                📋 All Competitions
              </button>

              <button
                onClick={handleOpenReels}
                className="action-button"
              >
                🎬 View Media Sources
              </button>

              <button
                onClick={() => navigate(`/competitions/${id}/submissions`)}
                className="action-button"
              >
                📑 View All Submissions
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Modal */}
      <SubmissionEntryModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        competitionId={id}
        competitionStatus={competition.status}
        submissionPlatform={submissionPlatform}
        hashtagLabel={hashtagLabel}
        availableSubmissionTypes={availableSubmissionTypes}
        userId={user?.id}
      />
    </div>
  );
};

function getMediaSources(competition) {
  if (!competition) {
    return [];
  }

  if (Array.isArray(competition.mediaSources) && competition.mediaSources.length > 0) {
    return competition.mediaSources.filter(function(source) {
      return source && source.platform && source.url;
    });
  }

  if (competition.youtubeLink) {
    return [
      {
        platform: 'youtube',
        url: competition.youtubeLink,
        videoId: competition.youtubeVideoId || null,
        type: 'full'
      }
    ];
  }

  return [];
}

function getPrimaryIndex(index, length) {
  if (!Number.isInteger(index) || index < 0 || index >= length) {
    return 0;
  }
  return index;
}

function formatHashtag(hashtag) {
  if (!hashtag) {
    return '#requiredHashtag';
  }
  return hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
}

function formatSubmissionTypes(types) {
  if (!Array.isArray(types) || types.length === 0) {
    return '';
  }

  const labels = { short: 'Short Video', full: 'Full Video', audio: 'Audio-based' };
  return types.map((t) => labels[t] || t).join(', ');
}

function formatPrizeLabel(prize) {
  if (typeof prize === 'string') {
    return prize;
  }

  if (!prize || typeof prize !== 'object') {
    return 'Prize';
  }

  const title = prize.title || prize.name || 'Prize';
  const cost = Number(prize.cost != null ? prize.cost : (prize.amount != null ? prize.amount : 0));
  const description = prize.description ? ` - ${prize.description}` : '';

  if (Number.isFinite(cost) && cost > 0) {
    return `${title} (${cost.toLocaleString('en-US')})${description}`;
  }

  return `${title}${description}`;
}

function capitalize(value) {
  if (!value || typeof value !== 'string') {
    return 'YouTube';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getSubmissionPlatform(primarySource, defaultPlatform) {
  if (primarySource && primarySource.platform) {
    return capitalize(primarySource.platform);
  }
  return capitalize(defaultPlatform || 'youtube');
}

function getAvailableSubmissionTypes(competition, mediaSources) {
  const supportedTypes = ['short', 'full', 'audio'];

  if (Array.isArray(competition?.allowedSubmissionTypes) && competition.allowedSubmissionTypes.length > 0) {
    const explicitTypes = competition.allowedSubmissionTypes.filter(function(type) {
      return supportedTypes.includes(type);
    });

    if (explicitTypes.length > 0) {
      return Array.from(new Set(explicitTypes));
    }
  }

  if (!Array.isArray(mediaSources) || mediaSources.length === 0) {
    return [];
  }

  const allowed = mediaSources
    .map(function(source) {
      return source && source.type ? source.type : null;
    })
    .filter(function(type) {
      return supportedTypes.includes(type);
    });

  return Array.from(new Set(allowed));
}

function isEditableCompetitionStatus(status) {
  if (!status) {
    return true;
  }

  return status === 'draft' || status === 'active';
}

function canCurrentUserEditCompetition(competition, user, isAdmin) {
  if (!competition || !user) {
    return false;
  }

  if (competition.createdBy === user.id) {
    return true;
  }

  return typeof isAdmin === 'function' ? isAdmin() : false;
}

function getValidRate(validSubmissions, totalSubmissions) {
  if (!totalSubmissions) {
    return 0;
  }

  return Math.round((validSubmissions / totalSubmissions) * 100);
}

function getUrgencyLabel(status, endDate) {
  if (status === 'completed') {
    return 'Competition ended';
  }

  if (status === 'judging') {
    return 'Judging in progress';
  }

  if (status !== 'active') {
    return 'Not accepting entries right now';
  }

  const endTimestamp = Number(endDate || 0);
  if (!endTimestamp) {
    return 'Timeline to be announced';
  }

  const remainingMs = endTimestamp - Date.now();
  if (remainingMs <= 0) {
    return 'Closing soon';
  }

  const daysLeft = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  if (daysLeft <= 1) {
    return 'Last 24 hours to enter';
  }

  return `${daysLeft} days left to submit`;
}

function getSubmissionMode(totalSubmissions) {
  const total = Number(totalSubmissions || 0);
  if (total < 5) {
    return 'EMPTY';
  }

  if (total < 20) {
    return 'EARLY';
  }

  if (total < 100) {
    return 'GROWING';
  }

  return 'MATURE';
}

function getCompetitionPhase(status) {
  if (status === 'completed') {
    return 'COMPLETED';
  }

  if (status === 'judging') {
    return 'JUDGING';
  }

  return 'ACTIVE';
}

function getEntriesSectionTitle(phase) {
  if (phase === 'JUDGING') {
    return 'Top entries under review';
  }

  if (phase === 'COMPLETED') {
    return 'Top performers';
  }

  return 'Recent submissions';
}

function getActiveVisibleEntries(visibilityType, submissionEntries) {
  const submissions = Array.isArray(submissionEntries) ? submissionEntries : [];
  if (visibilityType === 'hidden') {
    return [];
  }

  if (submissions.length < 5) {
    return submissions;
  }

  return submissions.slice(0, 8);
}

function sortEntriesByRecency(submissions) {
  if (!Array.isArray(submissions)) {
    return [];
  }

  return [...submissions].sort(function(a, b) {
    return Number(b.submittedAt || 0) - Number(a.submittedAt || 0);
  });
}

function getPulseLabel(phase, mode, submissionsLast24Hours) {
  if (phase === 'COMPLETED') {
    return 'Competition Peak';
  }

  if (phase === 'JUDGING') {
    return 'Under Review';
  }

  if (mode === 'GROWING') {
    return 'Momentum Building';
  }

  if (mode === 'EARLY') {
    return 'Early Stage Advantage';
  }

  if (submissionsLast24Hours > 0) {
    return 'Fresh Momentum';
  }

  return 'New Opportunity';
}

function getJoinPromptByMode(phase, mode, visibilityType) {
  if (phase === 'COMPLETED') {
    return 'Strong competition and clear ranking signals. High quality entries stand out.';
  }

  if (phase === 'JUDGING') {
    return 'Shortlisted entries are being reviewed. Final rankings will be published soon.';
  }

  if (visibilityType === 'hidden') {
    return 'Active phase privacy is enabled. Focus on your own entries and keep improving.';
  }

  if (mode === 'GROWING') {
    return 'Creator interest is accelerating. Submit now to ride the trend.';
  }

  if (mode === 'EARLY') {
    return 'Low crowd pressure right now. Early entrants can gain strong visibility.';
  }

  return 'Be the first to participate and set the trend 🚀';
}

function getActivitySignal(phase, mode, visibilityType) {
  if (phase === 'COMPLETED') {
    return 'Results are fully transparent now';
  }

  if (phase === 'JUDGING') {
    return 'Top entries are being evaluated';
  }

  if (visibilityType === 'hidden') {
    return 'Submissions are hidden during active phase';
  }

  if (mode === 'EMPTY') {
    return 'Creators are joining';
  }

  if (mode === 'EARLY') {
    return 'New entries are being added';
  }

  if (mode === 'GROWING') {
    return 'Creators are joining in high volume';
  }

  return 'Competition is active and transparent';
}

function formatApproxSubmissionCount(totalSubmissions) {
  const total = Number(totalSubmissions || 0);
  if (total < 20) {
    return '20+ submissions';
  }

  if (total < 100) {
    const rounded = Math.max(20, Math.floor(total / 10) * 10);
    return `${rounded}+ submissions`;
  }

  return `${Math.floor(total / 25) * 25}+ submissions`;
}

function getEntryUserLabel(entry) {
  if (!entry) {
    return 'Participant';
  }

  if (entry.user && typeof entry.user === 'object') {
    return entry.user.username || entry.user.name || entry.user.email || 'Participant';
  }

  if (entry.userId && typeof entry.userId === 'object') {
    return entry.userId.username || entry.userId.name || entry.userId.email || 'Participant';
  }

  if (entry.userId && typeof entry.userId === 'string') {
    return `User ${entry.userId.slice(0, 6)}`;
  }

  return 'Participant';
}

function formatEntryScore(score) {
  const value = Number(score || 0);
  if (Number.isNaN(value)) {
    return '0.00';
  }

  return value.toFixed(2);
}

function getTimeRemaining(status, endDate) {
  if (status === 'completed') {
    return 'Completed';
  }

  const endTimestamp = Number(endDate || 0);
  if (!endTimestamp) {
    return 'TBD';
  }

  const diff = endTimestamp - Date.now();
  if (diff <= 0) {
    return 'Closing soon';
  }

  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  if (days > 0) {
    return `${days}d ${hours}h left`;
  }

  return `${hours}h left`;
}

function getParticipantFillPercent(participantCount, maxParticipants) {
  const max = Number(maxParticipants || 0);
  if (!max) {
    return 0;
  }

  const fill = Math.round((Number(participantCount || 0) / max) * 100);
  if (fill < 0) {
    return 0;
  }

  if (fill > 100) {
    return 100;
  }

  return fill;
}

function getDescriptionPreview(description, maxLength) {
  if (!description || typeof description !== 'string') {
    return '';
  }

  if (description.length <= maxLength) {
    return description;
  }

  return `${description.slice(0, maxLength).trim()}...`;
}

function getBenefitChips(shortlistSize) {
  return [
    `🏆 Top ${shortlistSize || 20}`,
    '🎯 Multi-entry',
    '🔥 Fair'
  ];
}

function getDisplayedItems(items, showAll, initialCount) {
  if (!Array.isArray(items)) {
    return [];
  }

  if (showAll) {
    return items;
  }

  return items.slice(0, initialCount);
}

function AccordionSection({ title, isOpen, onToggle, meta, children }) {
  return (
    <section className={`accordion-section ${isOpen ? 'open' : ''}`}>
      <button type="button" className="accordion-toggle" onClick={onToggle}>
        <span>
          <strong>{title}</strong>
          {meta && <small>{meta}</small>}
        </span>
        <span className="accordion-icon" aria-hidden="true">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && <div className="accordion-body">{children}</div>}
    </section>
  );
}

function DetailItem({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="detail-item-card">
      <span className="detail-item-label">{label}</span>
      <strong className="detail-item-value">{value}</strong>
    </div>
  );
}

export default CompetitionDetailPage;
